import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const RadiographyCoursePage: React.FC = () => {
  const radiographyData = {
    title: "Bachelor of Science in Radiography and Imaging Technology",
    shortName: "B.Sc Radiography",
    duration: "3 Years",
    description: "A specialized healthcare program focusing on medical imaging techniques including X-ray, CT, MRI, ultrasound, and other diagnostic imaging modalities.",
    overview: "B.Sc Radiography and Imaging Technology prepares students to become skilled radiologic technologists who operate sophisticated imaging equipment to help diagnose medical conditions. The program covers radiation physics, anatomy, imaging techniques, patient care, and radiation safety. Students learn to perform various imaging procedures including conventional radiography, computed tomography, magnetic resonance imaging, and ultrasonography while ensuring patient safety and image quality.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology/Mathematics",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Medical fitness certificate required",
      "Good physical health and stamina",
      "Normal color vision essential"
    ],
    careerProspects: {
      roles: [
        "Radiographer",
        "Medical Imaging Technologist",
        "CT Scan Technician",
        "MRI Technician",
        "Ultrasound Technician",
        "Mammography Technologist",
        "Nuclear Medicine Technologist",
        "Radiation Safety Officer",
        "Imaging Equipment Specialist",
        "Radiology Department Supervisor"
      ],
      industries: [
        "Hospitals and Clinics",
        "Diagnostic Imaging Centers",
        "Research Institutions",
        "Medical Equipment Companies",
        "Government Health Services",
        "Mobile Imaging Services",
        "Veterinary Clinics",
        "Industrial Radiography",
        "Teaching Institutions",
        "Telemedicine Companies"
      ],
      averageSalary: "₹3-7 LPA (varies by specialization and experience)",
      topRecruiters: [
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Max Healthcare",
        "AIIMS",
        "Tata Memorial Hospital",
        "Philips Healthcare",
        "GE Healthcare",
        "Siemens Healthineers",
        "Government Hospitals",
        "Diagnostic Chains"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy",
          "Human Physiology",
          "Radiation Physics",
          "Basic Electronics",
          "Mathematics",
          "English Communication",
          "Computer Applications",
          "Medical Terminology"
        ]
      },
      {
        year: 2,
        subjects: [
          "Radiographic Techniques",
          "Pathology",
          "Contrast Studies",
          "Radiation Protection",
          "Quality Assurance",
          "Patient Care",
          "Darkroom Techniques",
          "Equipment Maintenance"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Imaging",
          "CT and MRI Technology",
          "Ultrasound Technology",
          "Nuclear Medicine",
          "Digital Radiography",
          "Clinical Training",
          "Research Methodology",
          "Professional Ethics"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc Radiography",
      "Merit-based selection or entrance examination",
      "Medical fitness examination including vision test",
      "Document verification",
      "Fee payment and admission",
      "Orientation and safety training",
      "Begin academic and practical training",
      "Complete mandatory clinical internship"
    ],
    topColleges: [
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹8,000 PA",
        ranking: "Premier Government Institute"
      },
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu",
        fees: "₹1.5 LPA",
        ranking: "Top Private Medical College"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka",
        fees: "₹3 LPA",
        ranking: "Deemed University"
      },
      {
        name: "JSS Medical College",
        location: "Mysore, Karnataka",
        fees: "₹2.5 LPA",
        ranking: "Well Established"
      },
      {
        name: "Government Medical College",
        location: "Thiruvananthapuram, Kerala",
        fees: "₹15,000 PA",
        ranking: "Government College"
      }
    ],
    skills: [
      "Imaging Equipment Operation",
      "Radiation Safety",
      "Patient Positioning",
      "Image Quality Assessment",
      "Technical Problem Solving",
      "Communication Skills",
      "Attention to Detail",
      "Physical Stamina",
      "Computer Proficiency",
      "Emergency Response",
      "Teamwork",
      "Continuous Learning"
    ],
    futureScope: "Medical imaging technology is rapidly advancing with AI-assisted diagnostics, 3D imaging, interventional radiology, and portable imaging devices. Future opportunities include specialization in advanced modalities like PET-CT, cardiac imaging, pediatric imaging, and emerging technologies like AI-powered image analysis. The integration of artificial intelligence and machine learning in medical imaging is creating new career paths in imaging informatics and technology development.",
    faqs: [
      {
        question: "Is there radiation exposure risk in this profession?",
        answer: "While radiographers work with radiation, strict safety protocols, protective equipment, and monitoring systems ensure minimal exposure. Following proper safety procedures makes it a safe profession."
      },
      {
        question: "What are the physical requirements for radiography?",
        answer: "Radiographers need good physical health, normal color vision, ability to lift patients, and stamina to stand for long periods. Manual dexterity and good hand-eye coordination are also important."
      },
      {
        question: "Can I specialize in specific imaging modalities?",
        answer: "Yes, you can specialize in areas like CT, MRI, mammography, ultrasound, nuclear medicine, or interventional radiology through additional training and certification programs."
      },
      {
        question: "What are the career advancement opportunities?",
        answer: "Career advancement includes becoming a senior technologist, department supervisor, applications specialist for equipment manufacturers, or moving into teaching and training roles."
      },
      {
        question: "Is there scope for higher education?",
        answer: "Yes, you can pursue M.Sc in Medical Imaging Technology, MBA in Hospital Administration, or specialized diplomas in advanced imaging techniques."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {radiographyData.title} ({radiographyData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={radiographyData} />
      </div>
    </div>
  );
};

export default RadiographyCoursePage;